import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import SummaryCard from "../../components/SummaryCard";
import HeatmapGrid from "../../components/HeatmapGrid";
import RackDetailModal from "../../components/RackDetailModal";
import RackDimensionHeatmap from "../../components/RackDimensionHeatmap";

import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from "recharts";

import { fetchMasterWarehouses } from "../raking/warehouseSlice";

import {
    fetchRacks,
    fetchRecommendation,
    fetchRackDetail,
    clearDetail,
    fetchDashboard,
    fetchPalletCapacity,
    searchRacks
} from "./dashboardSlice";

export default function Dashboard() {

    const dispatch = useDispatch();

    const {
        summary,
        racks,
        recommendation,
        rackDetail,
        palletCapacity,
        searchRacks: rackSearchResult
    } = useSelector((state) => state.dashboard);

    const [selectedRack, setSelectedRack] = useState(null);

    const masterWarehouses = useSelector(
        (state) => state.masterWarehouses
    );

    const [showDueDateModal, setShowDueDateModal] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState("");
    const [showDimensionModal, setShowDimensionModal] = useState(false);
    const [selectedRackDimension, setSelectedRackDimension] = useState(null);
    const warehouses = masterWarehouses?.data || [];
    const [searchRack, setSearchRack] = useState("");

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [page, setPage] = useState(1);

    const itemsPerPage = 10;

    // FILTER DATA
    const filteredData = useMemo(() => {

        return (
            palletCapacity?.bin_locations?.filter((item) =>
                item.kode_palet
                    ?.toLowerCase()
                    .includes(search.toLowerCase())
            ) || []
        );

    }, [palletCapacity, search]);

    // PAGINATION
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {

        dispatch(fetchRacks(selectedWarehouse));

        dispatch(fetchRecommendation(selectedWarehouse));

        dispatch(fetchDashboard(selectedWarehouse));

        dispatch(fetchPalletCapacity({
            warehouse_id: selectedWarehouse,
            search,
            page,
        }));

        dispatch(fetchMasterWarehouses());

    }, [dispatch, selectedWarehouse, search, currentPage, page]);

    const handleClickRack = (rack) => {

        setSelectedRack(rack);

        dispatch(
            fetchRackDetail({
                id: rack.racking_id,
                code: rack.level_code
            })
        );
    };

    const closeModal = () => {

        setSelectedRack(null);

        dispatch(clearDetail());
    };

    /*const openDimensionModal = (rack) => {

        setSelectedRackDimension(rack);

        dispatch(fetchRackDetail({
            id: rack.id,
            code: rack.code
        }));

        setShowDimensionModal(true);
    };*/

    const openDimensionModal = async (rack) => {

        setSelectedRackDimension(rack);

        const result = await dispatch(fetchRackDetail({
            id: rack.id,
            code: rack.code
        }));

        //console.log("RESULT", result.payload);

        if (result.payload?.length > 0) {
            setShowDimensionModal(true);
        }
    };

    const closeDimensionModal = () => {

        setSelectedRackDimension(null);

        setShowDimensionModal(false);
    };

    const capacitySummary = palletCapacity?.summary || {};

    useEffect(() => {

        const timer = setTimeout(() => {

            if (searchRack.trim()) {

                dispatch(
                    searchRacks({
                        warehouse_id: selectedWarehouse,
                        keyword: searchRack
                    })
                );

            }

        }, 500);

        return () => clearTimeout(timer);

    }, [searchRack, selectedWarehouse]);

    const rackData =
        searchRack.trim().length > 0
            ? rackSearchResult
            : racks;

    return (
        <div className="p-2 bg-gray-100 min-h-screen rounded-lg">

            {/* FILTER */}
            <div className="bg-white p-4 rounded-2xl shadow mb-6 flex items-center gap-4">

                <div>

                    <label className="block text-sm font-semibold mb-1">
                        Filter Warehouse
                    </label>

                    <select
                        value={selectedWarehouse}
                        onChange={(e) =>
                            setSelectedWarehouse(e.target.value)
                        }
                        className="border rounded-lg px-3 py-2 min-w-[220px]"
                    >

                        <option value="">
                            Semua Warehouse
                        </option>

                        {warehouses.map((w) => (
                            <option
                                key={w.id}
                                value={w.id}
                            >
                                {w.name}
                            </option>
                        ))}

                    </select>

                </div>

            </div>

            {/* SUMMARY */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">

                <SummaryCard
                    title="Total Rack"
                    value={summary.total_rack}
                    icon="📦"
                />

                <SummaryCard
                    title="Total Palet"
                    value={summary.total_palet}
                    icon="📊"
                />

                <div
                    onClick={() => setShowDueDateModal(true)}
                    className="cursor-pointer"
                >
                    <SummaryCard
                        title="Due Date"
                        value={capacitySummary?.total_due_date || 0}
                        icon="⚠️"
                    />
                </div>
                
                <SummaryCard
                    title="Occupancy"
                    value={`${capacitySummary.used_percent || 0}%`}
                    icon="🏭"
                />

            </div>

            {/* PALLET CAPACITY */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">

                {/* BAR CHART */}
                <div className="bg-white p-5 rounded-2xl shadow">

                    <div className="flex items-center justify-between mb-4">

                        <h2 className="font-bold text-lg">
                            📦 Kapasitas Racking
                        </h2>

                        <div className="text-sm text-gray-500">
                            Total Rack: {palletCapacity?.racks_locations?.length || 0}
                        </div>

                    </div>

                    <ResponsiveContainer
                        width="100%"
                        height={300}
                    >

                        <BarChart
                            data={palletCapacity?.racks_locations || []}
                        >

                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis dataKey="racks_name" />

                            <YAxis />

                            <Tooltip />

                            <Legend />

                            <Bar
                                dataKey="total_capacity"
                                name="Capacity"
                                fill="#3b82f6"
                            />

                            <Bar
                                dataKey="used_capacity"
                                name="Used"
                                fill="#ef4444"
                            />

                            <Bar
                                dataKey="available_capacity"
                                name="Available"
                                fill="#22c55e"
                            />

                        </BarChart>

                    </ResponsiveContainer>

                    {/*<div className="grid grid-cols-2 gap-4 mt-4">

                        <div className="bg-red-50 p-4 rounded-xl">

                            <div className="text-sm text-gray-500">
                                Used Capacity
                            </div>

                            <div className="text-2xl font-bold text-red-600">
                                {capacitySummary.used_qty || 0}
                            </div>

                            <div className="text-sm">
                                {capacitySummary.used_percent || 0}%
                            </div>

                        </div>

                        <div className="bg-green-50 p-4 rounded-xl">

                            <div className="text-sm text-gray-500">
                                Available Capacity
                            </div>

                            <div className="text-2xl font-bold text-green-600">
                                {capacitySummary.available_qty || 0}
                            </div>

                            <div className="text-sm">
                                {capacitySummary.available_percent || 0}%
                            </div>

                        </div>

                    </div>*/}

                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">

                <div className="bg-white p-5 rounded-2xl shadow">

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

                        <h2 className="font-bold text-lg">
                            📍 Lokasi Palet
                        </h2>

                        <div className="flex items-center gap-3">

                            <input
                                type="text"
                                placeholder="Cari nomor pallet..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="border rounded-lg px-3 py-2 text-sm w-64"
                            />

                            <div className="text-sm text-gray-500">
                                Total: {filteredData.length}
                            </div>

                        </div>

                    </div>

                    <div className="overflow-auto">

                        <table className="w-full border-collapse">

                            <thead>

                                <tr className="bg-gray-100">

                                    <th className="border p-2 text-left">
                                        Rack
                                    </th>

                                    <th className="border p-2 text-left">
                                        Level
                                    </th>

                                    <th className="border p-2 text-left">
                                        Pallet
                                    </th>

                                    <th className="border p-2 text-left">
                                        SKU
                                    </th>

                                    <th className="border p-2 text-left">
                                        Item Name
                                    </th>

                                    <th className="border p-2 text-left">
                                        Batch
                                    </th>

                                    <th className="border p-2 text-center">
                                        Qty
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {paginatedData.length > 0 ? (

                                    paginatedData.map((item, i) => (

                                        <tr
                                            key={i}
                                            className="hover:bg-gray-50"
                                        >

                                            <td className="border p-2 font-semibold">
                                                {item.racks_name}
                                            </td>

                                            <td className="border p-2">
                                                {item.level_code}
                                            </td>

                                            <td className="border p-2">
                                                {item.kode_palet}
                                            </td>

                                            <td className="border p-2">
                                                {item.sku}
                                            </td>

                                            <td className="border p-2">
                                                {item.description}
                                            </td>

                                            <td className="border p-2">
                                                {item.batch_number}
                                            </td>

                                            <td className="border p-2 text-center">
                                                {item.qty}
                                            </td>

                                        </tr>

                                    ))

                                ) : (

                                    <tr>
                                        <td
                                            colSpan="7"
                                            className="text-center p-4 text-gray-500"
                                        >
                                            Data tidak ditemukan
                                        </td>
                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                    {/* PAGINATION */}
                    <div className="flex justify-center items-center gap-2 mt-4">

                        <button
                            disabled={currentPage === 1}
                            onClick={() =>
                                setCurrentPage((prev) => prev - 1)
                            }
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Prev
                        </button>

                        <span className="text-sm">
                            Page {currentPage} / {totalPages || 1}
                        </span>

                        <button
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() =>
                                setCurrentPage((prev) => prev + 1)
                            }
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Next
                        </button>

                    </div>

                </div>

            </div>


            {/* LIST RACK */}
            <div className="bg-white p-4 rounded-2xl shadow mb-6">

                <div className="flex items-center justify-between mb-4">

                    <h2 className="font-bold text-lg">
                        📦 List Rack
                    </h2>

                    <input
                        type="text"
                        placeholder="Cari Rack / Item Code / Item Name / Batch"
                        value={searchRack}
                        onChange={(e) => setSearchRack(e.target.value)}
                        className="border rounded-lg px-3 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div className="overflow-auto">

                    <table className="w-full border-collapse">

                        <thead>

                            <tr className="bg-gray-100">
                                <th className="border p-2 text-left">
                                    Rack Name
                                </th>

                                <th className="border p-2 text-center">
                                    Rows
                                </th>

                                <th className="border p-2 text-center">
                                    Columns
                                </th>

                                <th className="border p-2 text-center">
                                    Total Capacity
                                </th>

                                <th className="border p-2 text-center">
                                   Use Capacity
                                </th>

                                <th className="border p-2 text-center">
                                    % Capacity
                                </th>

                                <th className="border p-2 text-center">
                                    Action
                                </th>

                            </tr>

                        </thead>

                        <tbody>
                            {rackData?.length > 0 ? (
                                rackData.map((rack) => (
                                    <tr key={rack.id}>
                                        <td className="border p-2">
                                            {rack.racks_name}
                                        </td>

                                        <td className="border p-2 text-center">
                                            {rack.total_rows}
                                        </td>

                                        <td className="border p-2 text-center">
                                            {rack.total_columns}
                                        </td>

                                        <td className="border p-2 text-center">
                                            {rack.available_capacity}
                                        </td>

                                        <td className="border p-2 text-center">
                                            {rack.used_capacity}
                                        </td>

                                        <td className="border p-2 text-center">

                                            <div className="relative w-full bg-gray-200 rounded-full h-5 overflow-hidden">

                                                <div
                                                    className={`
                h-5
                ${rack.used_capacity >= rack.available_capacity
                                                            ? "bg-red-500"
                                                            : rack.used_capacity >= (rack.available_capacity * 0.7)
                                                                ? "bg-yellow-500"
                                                                : "bg-green-500"
                                                        }
            `}
                                                    style={{
                                                        width: `${Math.min(
                                                            (rack.used_capacity / rack.available_capacity) * 100,
                                                            100
                                                        )}%`
                                                    }}
                                                />

                                                <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-black">

                                                    {Math.round(
                                                        (rack.used_capacity / rack.available_capacity) * 100
                                                    ) || 0}%

                                                </div>

                                            </div>

                                        </td>

                                        <td className="border p-2 text-center">

                                            <button
                                                onClick={() =>
                                                    openDimensionModal(rack)
                                                }
                                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm"
                                            >
                                                Lihat Dimensi
                                            </button>

                                        </td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="border p-4 text-center text-gray-500"
                                    >
                                        Data rack tidak ditemukan
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* SIDE PANEL */}
            {/*<div className="grid grid-cols-2 gap-6">

                <div className="bg-white p-4 rounded-2xl shadow">

                    <h2 className="font-bold mb-3">
                        📦 Rekomendasi Slot
                    </h2>

                    {recommendation.rack_recommendation?.map((r) => (

                        <div
                            key={r.id}
                            className="border-b py-2"
                        >

                            {r.level_code} -
                            {Math.round(r.percentage)}%

                        </div>

                    ))}

                </div>

                <div className="bg-white p-4 rounded-2xl shadow">

                    <h2 className="font-bold mb-3">
                        ⏰ FEFO
                    </h2>

                    {recommendation.fefo?.map((f, i) => (

                        <div
                            key={i}
                            className="border-b py-2"
                        >

                            <div>{f.sku}</div>

                            <div className="text-xs">
                                Exp: {f.due_date}
                            </div>

                        </div>

                    ))}

                </div>

            </div>*/}

            {/* DETAIL MODAL */}
            <RackDetailModal
                rack={selectedRack}
                data={rackDetail}
                onClose={closeModal}
            />

            {/* DIMENSION MODAL */}
            {showDimensionModal && selectedRackDimension && (

                <RackDimensionHeatmap
                    rack={selectedRackDimension}
                    details={rackDetail}
                    onClose={closeDimensionModal}
                />

            )}

            {/* DUE DATE MODAL */}
            {showDueDateModal && (

                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">

                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">

                        {/* HEADER */}
                        <div className="flex items-center justify-between px-6 py-4 border-b">

                            <div>
                                <h2 className="text-xl font-bold">
                                    ⚠️ Daftar Due Date
                                </h2>

                                <p className="text-sm text-gray-500">
                                    Total Item: {palletCapacity?.daftar_duedate?.length || 0}
                                </p>
                            </div>

                            <button
                                onClick={() => setShowDueDateModal(false)}
                                className="text-gray-500 hover:text-black text-xl"
                            >
                                ✕
                            </button>

                        </div>

                        {/* CONTENT */}
                        <div className="overflow-auto max-h-[75vh]">

                            <table className="w-full border-collapse">

                                <thead className="sticky top-0 bg-gray-100 z-10">

                                    <tr>

                                        <th className="border p-3 text-left">
                                            SKU
                                        </th>

                                        <th className="border p-3 text-left">
                                            Item Name
                                        </th>

                                        <th className="border p-3 text-left">
                                            Batch
                                        </th>

                                        <th className="border p-3 text-left">
                                            Due Date
                                        </th>

                                        <th className="border p-3 text-center">
                                            Remaining
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {palletCapacity?.daftar_duedate?.length > 0 ? (

                                        palletCapacity.daftar_duedate.map((item, index) => {

                                            const dueDate = new Date(item.due_date);
                                            const today = new Date();

                                            const diffTime =
                                                dueDate.getTime() - today.getTime();

                                            const diffDays = Math.ceil(
                                                diffTime / (1000 * 60 * 60 * 24)
                                            );

                                            return (

                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50"
                                                >

                                                    <td className="border p-3">
                                                        {item.sku}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.product_name}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.batch_no}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.due_date}
                                                    </td>

                                                    <td className="border p-3 text-center">

                                                        <span
                                                            className={`
                                                    px-2 py-1 rounded-lg text-xs font-semibold
                                                    ${diffDays <= 30
                                                                    ? "bg-red-100 text-red-700"
                                                                    : diffDays <= 90
                                                                        ? "bg-yellow-100 text-yellow-700"
                                                                        : "bg-green-100 text-green-700"
                                                                }
                                                `}
                                                        >

                                                            {diffDays > 0
                                                                ? `${diffDays} hari`
                                                                : "Expired"}

                                                        </span>

                                                    </td>

                                                </tr>

                                            );
                                        })

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan="5"
                                                className="text-center p-6 text-gray-500"
                                            >
                                                Tidak ada data due date
                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}